import { useId, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';

/** Small, brand-styled building blocks shared across pages. */

/** The one password policy, shared by the fields and their validation. */
export const MIN_PASSWORD_LENGTH = 12;
export const isPasswordValid = (value: string): boolean => value.length >= MIN_PASSWORD_LENGTH;

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'muted';

/* The brand button: square, uppercase Oswald with wide tracking. Solid fills
   with green, hover to the deeper green; the ghost (secondary) is an ink
   outline that fills on hover; muted is a grey fill for low-emphasis, redo-type
   actions. Mirrors `.btn` on foundry.aldemis.com. */
const BUTTON_BASE =
  'inline-flex items-center justify-center px-5 py-2.5 font-title text-sm font-medium uppercase tracking-[0.14em] transition disabled:cursor-not-allowed';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-deep disabled:opacity-50',
  secondary:
    'bg-white text-secondary ring-2 ring-inset ring-secondary hover:bg-secondary hover:text-white disabled:opacity-50',
  danger: 'bg-red-700 text-white hover:bg-red-800 disabled:opacity-50',
  muted: 'bg-secondary/10 text-secondary hover:bg-secondary/20 disabled:opacity-50',
};

/** The button classes as a string, so a `<Link>`/`<a>` CTA can look identical. */
export function buttonClass(variant: ButtonVariant = 'primary'): string {
  return `${BUTTON_BASE} ${BUTTON_STYLES[variant]}`;
}

export function Button({
  variant = 'primary',
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button className={`${buttonClass(variant)} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Field({
  label,
  error,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <input
        className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        {...props}
      />
      {error ? <span className="text-sm text-red-700">{error}</span> : null}
    </label>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
    </svg>
  );
}

/** A password input with a reveal toggle. */
export function PasswordField({
  label,
  error,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string; error?: string }) {
  const [show, setShow] = useState(false);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          className="w-full rounded-md border border-secondary/20 bg-white px-3 py-2 pr-10 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-secondary/50 hover:text-secondary"
        >
          <EyeIcon off={show} />
        </button>
      </div>
      {error ? <span className="text-sm text-red-700">{error}</span> : null}
    </label>
  );
}

/** Live checklist of the password policy for a value being typed. */
export function PasswordRules({ value }: { value: string }) {
  const rules = [
    {
      label: `At least ${MIN_PASSWORD_LENGTH} characters`,
      ok: value.length >= MIN_PASSWORD_LENGTH,
    },
  ];
  const id = useId();
  return (
    <ul className="space-y-0.5 text-xs" aria-label="Password requirements">
      {rules.map((rule, i) => (
        <li
          key={`${id}-${i}`}
          className={`flex items-center gap-1.5 ${rule.ok ? 'text-primary' : 'opacity-60'}`}
        >
          <span aria-hidden="true">{rule.ok ? '✓' : '○'}</span>
          {rule.label}
        </li>
      ))}
    </ul>
  );
}

export function TextArea({
  label,
  hint,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      {hint ? <span className="text-xs opacity-60">{hint}</span> : null}
      <textarea
        className="min-h-24 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        {...props}
      />
    </label>
  );
}

export function Alert({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'info';
  children: ReactNode;
}) {
  const styles =
    kind === 'error'
      ? 'bg-red-50 text-red-800 ring-red-200'
      : 'bg-primary/10 text-secondary ring-primary/20';
  return <div className={`rounded-md px-3 py-2 text-sm ring-1 ${styles}`}>{children}</div>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm opacity-70" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-secondary/30 border-t-primary" />
      {label}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-lg bg-white p-6 shadow-sm ring-1 ring-secondary/5 ${className}`}>
      {children}
    </section>
  );
}

/** A coloured status pill. Neutral by default; callers map a status to a tone. */
export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

export type BadgeTone = 'neutral' | 'progress' | 'attention' | 'success' | 'danger';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral text-secondary/70',
  progress: 'bg-blue-100 text-blue-800',
  attention: 'bg-amber-100 text-amber-900',
  success: 'bg-primary/15 text-primary',
  danger: 'bg-red-100 text-red-800',
};
